import { createSlice } from '@reduxjs/toolkit'
import { HYDRATE } from 'next-redux-wrapper'
import {
  RegimeValues,
  DriedVegetablesValues,
  FishFrequencyValues,
  FruitsValues,
  MeatFrequencyValues,
  MomentValues,
  StarchyFoodsValues,
  StructureValues,
  SugarFrequencyValues,
} from 'src/types'

import type { RootState } from '../store'
import {
  getServiceState,
  getServiceReducers,
  ServiceValues,
} from '../../helpers/ReduxHelpers'

import { actionTypes } from './types'

export type OnboardingState = {
  tips: any
  stepsValues: any
  stepsWeight: any
  currentStepIndex: number
  onboard: ServiceValues
}

//
// Initial state
//

const initialState: OnboardingState = {
  tips: null,
  stepsValues: {
    people_count: 2,
    diet_practice: RegimeValues.NONE,
    course_composition: StructureValues.ENT_PLAT_DESSERT,
    express: false,
    course_range: StructureValues.ENT_PLAT_DESSERT,
    fruits_vegetables_quantity: FruitsValues.PORTIONS_5,
    starchy_foods_frequency: StarchyFoodsValues.PER_WEEK_7,
    dried_vegetables_frequency: DriedVegetablesValues.PER_WEEK_2,
    fish_frequency: FishFrequencyValues.PER_WEEK_2,
    meat_frequency: MeatFrequencyValues.PER_WEEK_2,
    drink_frequency: SugarFrequencyValues.NEVER,
    moment: MomentValues.LUNCH_AND_DINNER,
  },
  stepsWeight: {
    fruits_vegetables_quantity: 'optimal',
    starchy_foods_frequency: 'optimal',
    dried_vegetables_frequency: 'optimal',
    fish_frequency: 'optimal',
    meat_frequency: 'optimal',
    drink_frequency: 'optimal',
  },
  currentStepIndex: 0,
  onboard: getServiceState(),
}

//
// Slice (Actions & Reducers)
//

const onboardReducers = getServiceReducers('onboard')

const slice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    init: () => undefined,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveOnboardingValues: (_state, _action: actionTypes.saveOnboardingValues) =>
      undefined,
    setTips: (state, action: actionTypes.setTips) => {
      state.tips = action.payload
    },
    setCurrentStepIndex: (state, action: actionTypes.setCurrentStepIndex) => {
      state.currentStepIndex = action.payload
    },
    setStepValues: (state, action: actionTypes.setStepValues) => {
      state.stepsValues = {
        ...state.stepsValues,
        [action?.payload?.name ?? '']: action.payload.value,
      }
    },
    setStepWeight: (state, action: actionTypes.setStepWeight) => {
      state.stepsWeight = {
        ...state.stepsWeight,
        [action?.payload?.name ?? '']: action.payload.weight,
      }
    },
    onboardRequest: onboardReducers.request,
    onboardSuccess: onboardReducers.success,
    onboardError: onboardReducers.error,
    onboardReset: onboardReducers.reset,
  },
  extraReducers: {
    [HYDRATE]: (_state, action) => {
      return action.payload.onboarding
    },
  },
})

export const { reducer, actions } = slice

//
// Selectors
//

const root = (state: RootState) => state.onboarding
const stepsValues = (state: RootState) => root(state).stepsValues
const stepsWeight = (state: RootState) => root(state).stepsWeight
const currentStepIndex = (state: RootState) => root(state).currentStepIndex
const tips = (state: RootState) => root(state).tips
const onboard = (state: RootState) => root(state).onboard

export const selectors = {
  tips,
  stepsValues,
  stepsWeight,
  currentStepIndex,
  onboard,
}
